import {TableNode} from '@/link-components/table/utils/TableNode'
import {TableConfig, TableMark} from "@/link-components/table/utils/TableMark";

export function createPltTreeUtils(
	{
		mark,
		isLoading,
		tableConfig: {
			keyField,
			getChildren,
			lazy,
			according,
			autoExpandParent,
			checkStrictly,
			flatCheckedData,
		},
		emit,
		tableData,
		treeColumn,
	}: {
		mark: TableMark,
		isLoading: { value: boolean | undefined },
		tableConfig: TableConfig,
		emit: {
			expand: (node: TableNode) => void,
			collapse: (node: TableNode) => void,
			expandChange: (expandKeys: string[]) => void,

			check: (node: TableNode) => void,
			uncheck: (node: TableNode) => void,
			checkChange: (checkKeys: string[]) => void,
		},
		tableData: () => TableNode[],
		treeColumn: any,
	}) {

	const state = {
		currentNode: null as null | TableNode,
	}

	const utils = {
		/**
		 * 处理keys
		 * @author  韦胜健
		 * @date    2020/8/17 10:00
		 */
		handleKeys: async (keys: string | string[], handler: (value: string, index: number, array: string[]) => unknown) => {
			keys = Array.isArray(keys) ? keys : [keys]
			return await Promise.all(keys.map(handler))
		},
		/**
		 * 遍历所有的treeNode
		 * @author  韦胜健
		 * @date    2020/8/17 9:59
		 */
		iterateAll: (treeNodes: TableNode[] | null, fn: (node: TableNode) => void, iterateChildren?: Function): void => {
			if (!treeNodes) return
			treeNodes.forEach(treeNode => {
				fn(treeNode)
				if (!!treeNode.children && (!iterateChildren || iterateChildren(treeNode))) {
					utils.iterateAll(treeNode.children, fn, iterateChildren)
				}
			})
		},
		/**
		 * 通过 key 寻找TableNode
		 * @author  韦胜健
		 * @date    2020/8/17 9:59
		 */
		findNodeByKey: (key: string): TableNode | null => {
			const node = mark.node.getByKey(key)
			if (!node) {
				console.warn(`无法找到treeNode：${key}`, mark.node.state.map)
				return null
			}
			// @ts-ignore
			return node
		},
		/**
		 * 获取子节点数据异步方法
		 * @author  韦胜健
		 * @date    2020/3/31 15:21
		 */
		getChildrenAsync: (node: TableNode): Promise<TableNode[]> => {
			return new Promise((resolve) => {
				if (!node.id) {
					isLoading.value = true
				} else {
					node.loading(true)
				}
				getChildren!(node, (...results) => {

					if (!node.id) {
						isLoading.value = false
					} else {
						node.loading(false)
						node.loaded(true)
					}

					resolve(...results)
				})
			})
		},
		/**
		 * 懒加载模式下，第一次初始化数据
		 * @author  韦胜健
		 * @date    2020/8/17 10:05
		 */
		async initLazy() {
			/*if (!lazy) {
				return
			}
			dataModel.value = await utils.getChildrenAsync(rootNode)*/
		},
		/**
		 * 获取节点祖宗数组数据
		 * @author  韦胜健
		 * @date    2020/10/28 17:40
		 */
		getNodeAncestors: (node: TableNode) => {
			let ancestors: TableNode[] = []
			while (!!node) {
				ancestors.unshift(node)
				node = node.parentRef()
			}
			return ancestors
		}
	}

	const methods = {
		/*---------------------------------------expand-------------------------------------------*/

		/**
		 * 展开节点
		 * @author  韦胜健
		 * @date    2020/8/17 10:17
		 */
		async expand(keys: string | string[]) {
			await utils.handleKeys(keys, async key => {
				const node = utils.findNodeByKey(key)
				if (!node) return
				if (!node.isExpand) {
					if (
						lazy &&
						!node.isLoaded &&
						!node.isLeaf
					) {
						const children = await utils.getChildrenAsync(node)
						node.setChildren(children)
						await (window as any).LinkVue.nextTick()
					}

					const parent = node.parentRef()

					if (according) {
						if (!!parent && !!parent.children) {
							parent.children.forEach(child => child.id !== node.id && methods.collapse(child.id))
						}
					}

					node.expand(true)
					await (window as any).LinkVue.nextTick()
					emit.expand(node)
					emit.expandChange(mark.expand.getActiveKeys() as string[])

					if (autoExpandParent && !!parent && !!parent.id) {
						await methods.expand(parent.id)
					}
				}
			})
		},
		/**
		 * 收起节点
		 * @author  韦胜健
		 * @date    2020/8/17 10:17
		 */
		async collapse(keys: string | string[]) {
			await utils.handleKeys(keys, async key => {
				const node = utils.findNodeByKey(key)
				if (!node) return
				if (node.isExpand) {
					node.expand(false)
					await (window as any).LinkVue.nextTick()
					emit.collapse(node)
					emit.expandChange(mark.expand.getActiveKeys() as string[])
				}
			})
		},
		/**
		 * 开启/关闭节点
		 * @author  韦胜健
		 * @date    2020/8/17 10:20
		 */
		async toggleExpand(id: string) {
			const node = utils.findNodeByKey(id)
			if (!node) return node
			if (node.isExpand) {
				await methods.collapse(node.id)
			} else {
				await methods.expand(node.id)
			}
		},
		/**
		 * 开启所有节点
		 * @author  韦胜健
		 * @date    2020/8/17 10:21
		 */
		async expandAll() {
			const data = tableData()
			if (!!data && data.length > 0) {
				utils.iterateAll(data, node => methods.expand(node.id))
			}
		},
		/**
		 *
		 * @author  收起所有节点
		 * @date    2020/8/17 10:33
		 */
		async collapseAll() {
			mark.expand.state.map = window.Vue.observable({})
		},

		/**
		 * 根据keys选中节点
		 * @author  韦胜健
		 * @date    2020/8/17 15:20
		 */
		async check(keys: string | string[]) {
			await utils.handleKeys(keys, async key => {
				const node = utils.findNodeByKey(key)
				if (!node) return
				if (!node.isCheck) {
					node.check(true)

					if (!checkStrictly) {
						// 选中所有子节点
						utils.iterateAll(node.children || [], (child) => child.check(true))

						// 更新父节点状态，如果父节点所有的子节点都处于选中状态，则更新父节点为选中状态
						let parent = node.parentRef()
						while (!!parent && !!parent.id) {
							if ((parent.children || []).every(child => child.isCheck)) {
								parent.check(true)
								parent = parent.parentRef()
							} else {
								break
							}
						}
					}

					await (window as any).LinkVue.nextTick()
					emit.check(node)
					emit.checkChange(mark.check.getActiveKeys() as string[])
				}
			})
		},
		/**
		 * 根据keys取消选中节点
		 * @author  韦胜健
		 * @date    2020/8/17 15:33
		 */
		async uncheck(keys: string | string[]) {
			await utils.handleKeys(keys, async key => {
				const node = utils.findNodeByKey(key)
				if (!node) return
				if (node.isCheck) {
					node.check(false)
					if (!checkStrictly) {
						// 取消选中所有子节点
						utils.iterateAll(node.children || [], (child) => child.check(false))
						// 更新父节点状态，如果父节点所有的子节点都处于非选中状态，则更新父节点为非选中状态
						let parent = node.parentRef()
						while (!!parent && !!parent.id) {
							if (parent.isCheck) {
								parent.check(false)
								parent = parent.parentRef()
							} else {
								break
							}
						}
					}

					await (window as any).LinkVue.nextTick()
					emit.check(node)
					emit.checkChange(mark.check.getActiveKeys() as string[])
				}
			})
		},
		/**
		 * 根据key选中或者取消选中树节点
		 * @author  韦胜健
		 * @date    2020/8/17 15:39
		 */
		async toggleCheck(id: string) {
			const node = utils.findNodeByKey(id)
			if (!node) return
			if (!node.isCheckable) return
			if (node.isCheck) {
				await methods.uncheck(id)
			} else {
				await methods.check(id)
			}
		},
		/**
		 * 选中所有节点
		 * @author  韦胜健
		 * @date    2020/8/17 15:40
		 */
		checkAll() {
			utils.iterateAll(tableData(), (node: TableNode) => node.check(true))
		},
		/**
		 * 取消选中所有节点
		 * @author  韦胜健
		 * @date    2020/8/17 15:40
		 */
		uncheckAll() {
			mark.check.state.map = window.Vue.observable({})
		},
		/**
		 * 获取选中的数据
		 * @author  韦胜健
		 * @date    2020/8/17 15:41
		 */
		getCheckedData() {

			const {showCheckbox} = treeColumn

			if (!showCheckbox) {
				/*单选*/
				const {currentNode: node} = state
				if (!node) {
					return null
				}
				if (!flatCheckedData) {
					/*获取选中的数据*/
					return node.row
				} else {
					/*获取选中的数据（数组，包括祖宗数据）*/
					return utils.getNodeAncestors(node).map(n => n.row)
				}

			} else {
				if (!flatCheckedData) {
					/*获取选中的数据*/
					let ret: object[] = []
					utils.iterateAll(tableData(), (node: TableNode) => {
						if (node.isCheck) ret.push(node.row)
					})
					return ret
				} else {
					/*获取选中的数据（数组，包括祖宗数据）*/
					let list: any[] = []
					utils.iterateAll(tableData(), (node: TableNode) => {
						if (node.isCheck && node.isLeaf) list.push(utils.getNodeAncestors(node).map(n => n.row))
					})
					return list
				}
			}
		},
	}

	const handler = {
		clickExpandIcon: async (e: MouseEvent, nodeId: string) => {
			e.stopPropagation()
			await methods.toggleExpand(nodeId)
		},
		clickCheckbox: async (e: MouseEvent, nodeId: string) => {
			e.stopPropagation()
			await methods.toggleCheck(nodeId)
		},
		rowClick: ({row: {row}}: any) => state.currentNode = utils.findNodeByKey(row[keyField!]),
	}

	treeColumn.baseTable.$on('rowClick', handler.rowClick)
	treeColumn.$on('hook:beforeDestroy', () => treeColumn.baseTable.$off('rowClick', handler.rowClick))

	return {
		state,
		utils,
		handler,
		methods,
		tableData,
	}
}